import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import {
  getPaidAmount,
  isPaymentDeadlineExpired,
  isTourDocumentCustomer,
  useTourDocument,
} from 'entities/tour/models/document'

import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat } from 'shared/lib/dateFormat'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { PageLoader } from 'shared/ui/page-loader'

import { DocumentState } from '../../ui/document-state'

import s from './styles.module.scss'

const DocumentPaymentHistoryPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { documentId } = useParams()
  const {
    data: document,
    isLoading,
    isError,
    refetch,
  } = useTourDocument(documentId)

  const handleGoBack = () => navigate(-1)

  if (document) {
    if (!isTourDocumentCustomer(document)) {
      return (
        <Navigate
          to={RoutingPaths.tours.documentDetails.path(document.id)}
          replace
        />
      )
    }

    const paidAmount = getPaidAmount(document)
    const payments = document.payments.filter(
      (payment) => payment.status !== 'CREATED',
    )
    const summary = [
      document.amount !== null && ['tourPrice', document.amount],
      paidAmount !== null && ['paid', paidAmount],
      document.remainingAmount !== null && [
        'remaining',
        document.remainingAmount,
      ],
    ].filter(Boolean) as [string, number][]
    const paymentDueDate = document.fullPaymentBeforeDate
      ? dateFormat(document.fullPaymentBeforeDate, DATE_FORMAT_LIST.FULL_DATE)
      : null
    const isPaymentExpired =
      document.remainingAmount !== null &&
      document.remainingAmount > 0 &&
      isPaymentDeadlineExpired(document.fullPaymentBeforeDate)

    return (
      <main className={s.page}>
        <HeaderGoBack
          isFixed={false}
          variant="grey"
          buttonWhite
          onGoBack={handleGoBack}
        >
          <h1 className={`${s.title} bold-16`}>{t('tourDetails.payments')}</h1>
        </HeaderGoBack>

        <div className={s.content}>
          {(summary.length > 0 || paymentDueDate) && (
            <section className={s.card}>
              <div className={s.intro}>
                <h2 className="bold-16">
                  {t('tourPaymentHistory.information')}
                </h2>
                {document.remainingAmount === 0 ? (
                  <p className="regular-14">{t('tourPaymentHistory.noDebt')}</p>
                ) : isPaymentExpired ? (
                  <p className={`${s.deadline} regular-14`}>
                    {t('tourDetails.paymentExpired')}
                  </p>
                ) : (
                  paymentDueDate && (
                    <p className={`${s.deadline} regular-14`}>
                      {t('tourPaymentHistory.paymentDueDate', {
                        date: paymentDueDate,
                      })}
                    </p>
                  )
                )}
              </div>
              {summary.length > 0 && (
                <dl className={s.summary}>
                  {summary.map(([label, value]) => (
                    <div
                      className={s.summaryRow}
                      key={label}
                    >
                      <dt className="medium-14">
                        {t(`tourPaymentHistory.${label}`)}
                      </dt>
                      <dd className="semi-bold-14">
                        {value.toLocaleString('ru-RU')}
                        <u>с</u>
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          )}

          {payments.length > 0 && (
            <section className={s.card}>
              <h2 className="bold-16">{t('tourPaymentHistory.history')}</h2>
              <ul className={s.payments}>
                {payments.map((payment, index) => (
                  <li
                    className={s.payment}
                    key={payment.id ?? payment.transactionId ?? index}
                  >
                    <div>
                      {payment.paymentDate && (
                        <p className="medium-14">
                          {dateFormat(payment.paymentDate, 'D MMMM, YYYY')}
                        </p>
                      )}
                      <span
                        className={`${payment.status === 'SUCCESS' ? s.status : s.otherStatus} medium-12`}
                      >
                        {t(
                          `tourPaymentHistory.operationStatus.${payment.status}`,
                        )}
                      </span>
                    </div>
                    <div className={s.paymentRight}>
                      {payment.amount !== null && (
                        <p className="semi-bold-14">
                          {payment.amount.toLocaleString('ru-RU')}
                          <u>с</u>
                        </p>
                      )}
                      {payment.paymentMethod && (
                        <span className="medium-12">
                          {t(
                            `tourPaymentHistory.${
                              payment.paymentMethod === 'CASH'
                                ? 'officePayment'
                                : 'mtravelPayment'
                            }`,
                          )}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>
    )
  }

  if (documentId && isLoading) return <PageLoader />

  if (!documentId || isError || !document) {
    return (
      <DocumentState
        onGoBack={handleGoBack}
        onRetry={() => void refetch()}
      />
    )
  }

  return null
}

export default DocumentPaymentHistoryPage
